import { useEffect, useMemo, useState } from 'react'
import { Calendar, RefreshCw, Search } from 'lucide-react'
import type { QuickBooksGlSummaryResponse, ServerApiClient } from '../../../data/ServerApiClient'
import {
  DEFAULT_GL_DETAIL_COLUMNS,
  GL_DETAIL_NUMERIC_COLUMNS,
  glDetailColumnLabel,
  glDetailColumnsFromRows,
  normalizeGlDetailRow,
  trimGlStr,
} from '../quickBooksGlDetailShared'
import { formatGlCell, formatGlNumber, selectVisibleGlColumns } from './cfoGlDetailQuery'
import { CfoControlRow, CfoLoadingNote } from './CfoPagePrimitives'

// CfoGlDetailServerFallback — the governed, server-backed GL detail browser.
// Runs entirely on the governed compact `gl-summary` contract (no browser
// DuckDB, no parquet materialization), so it works on mobile-constrained
// runtimes and whenever the local GL cache cannot load. Search + month scope +
// the first N matching rows with totals; deliberately simpler than the full
// explorer, which stays the desktop path.

const FALLBACK_ROW_LIMIT = 100
const FALLBACK_VISIBLE_COLUMNS = Array.from(new Set([...DEFAULT_GL_DETAIL_COLUMNS, 'debit', 'credit', 'amount']))
const SEARCH_DEBOUNCE_MS = 300

export type CfoGlDetailServerFallbackProps = {
  appApi: ServerApiClient
  workbookId: string | null
  /** Why the fallback is showing — sets the lead-in copy. */
  reason: 'canonical' | 'mobile' | 'local_unavailable'
  /** Extra detail line (e.g. the underlying materialization error). */
  detail?: string | null
  /** Seed scope, e.g. from a statement drilldown handoff. */
  initialSearch?: string | null
  /** Seed month scope (YYYY-MM or YYYY-MM-DD). */
  initialMonth?: string | null
  /**
   * Retry the full local ledger. Supplied when the fallback is showing because
   * the local engine failed rather than because the device cannot run it, so a
   * transient failure does not strand the page on the capped server view until
   * a manual browser reload.
   */
  onRetryLocal?: (() => void) | null
}

const toMonthValue = (value: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})/.exec(String(value || '').trim())
  return match ? `${match[1]}-${match[2]}` : ''
}

const summaryBucketLine = (buckets: QuickBooksGlSummaryResponse['top_accounts']): string =>
  buckets.slice(0, 3).map((bucket) => `${bucket.label} ${formatGlNumber(bucket.amount)}`).join(' | ')

export function CfoGlDetailServerFallback({
  appApi,
  workbookId,
  reason,
  detail = null,
  initialSearch = null,
  initialMonth = null,
  onRetryLocal = null,
}: CfoGlDetailServerFallbackProps) {
  const [searchInput, setSearchInput] = useState(trimGlStr(initialSearch))
  const [search, setSearch] = useState(trimGlStr(initialSearch))
  const [month, setMonth] = useState(() => toMonthValue(initialMonth))
  const [summary, setSummary] = useState<QuickBooksGlSummaryResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [searchInput])

  useEffect(() => {
    const wb = trimGlStr(workbookId)
    if (!wb || (typeof (appApi as any).getCfoGlSummary !== 'function' && typeof appApi.getQuickBooksGlSummary !== 'function')) {
      setSummary(null)
      setError(wb ? 'The governed GL summary is not available in this session.' : 'This view is not bound to a workbook.')
      return
    }
    const controller = new AbortController()
    let cancelled = false
    setLoading(true)
    setError('')
    const getGlSummary = typeof (appApi as any).getCfoGlSummary === 'function'
      ? (appApi as any).getCfoGlSummary.bind(appApi)
      : appApi.getQuickBooksGlSummary.bind(appApi)
    getGlSummary(wb, {
        asOf: month ? `${month}-01` : null,
        search: search || null,
        limit: FALLBACK_ROW_LIMIT,
        signal: controller.signal,
      })
      .then((response: QuickBooksGlSummaryResponse) => {
        if (cancelled) return
        setSummary(response)
        setLoading(false)
      })
      .catch((fetchError: any) => {
        if (cancelled || fetchError?.name === 'AbortError') return
        setError(trimGlStr(fetchError?.message || fetchError) || 'The governed GL summary failed to load.')
        setLoading(false)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [appApi, month, search, workbookId])

  const rows = useMemo(
    () => (summary?.rows || []).map((row) => normalizeGlDetailRow(row)),
    [summary],
  )
  const columns = useMemo(() => {
    const allColumns = glDetailColumnsFromRows(rows)
    const visible = selectVisibleGlColumns(FALLBACK_VISIBLE_COLUMNS, allColumns)
    return visible.length ? visible : allColumns.slice(0, 8)
  }, [rows])
  const numericTotalColumns = useMemo(
    () => columns.filter((column) => GL_DETAIL_NUMERIC_COLUMNS.has(column) && Object.prototype.hasOwnProperty.call(summary?.totals || {}, column)),
    [columns, summary],
  )
  const buckets = useMemo(
    () => ([
      ['Top accounts', summary?.top_accounts || []],
      ['Top vendors', summary?.top_vendors || []],
      ['Top classes', summary?.top_classes || []],
    ] as Array<[string, QuickBooksGlSummaryResponse['top_accounts']]>).filter(([, list]) => list.length),
    [summary],
  )

  return (
    <div className="cfo-gl-page cfo-gl-server-fallback">
      <section className="cfo-gl-state cfo-gl-state-summary rise" role="status" aria-live="polite">
        <strong>
          {reason === 'canonical'
            ? 'Showing the reconciled general ledger.'
            : reason === 'mobile'
            ? 'Showing the simplified GL detail for mobile.'
            : 'Showing the simplified server GL detail.'}
        </strong>
        <span>
          {reason === 'canonical'
            ? detail || 'Search, filter, and inspect the provider-neutral ledger lines reconciled for this workbook.'
            : reason === 'mobile'
            ? 'The full explorer streams the entire ledger through local DuckDB, which needs a desktop browser. This view searches the governed server ledger instead.'
            : detail || 'The local GL workspace could not load, so this view searches the governed server ledger instead.'}
          {` Results are capped at the first ${FALLBACK_ROW_LIMIT} matching rows.`}
        </span>
        {onRetryLocal ? (
          <button type="button" className="cfo-shell-chip" onClick={onRetryLocal}>
            <RefreshCw size={15} /> Retry full ledger
          </button>
        ) : null}
      </section>
      <CfoControlRow>
        <label className="cfo-gl-search">
          <Search size={16} />
          <input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search account, vendor, memo…"
            aria-label="Search GL detail"
          />
        </label>
        <label className="cfo-gl-date-input">
          <Calendar size={15} />
          <input
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            aria-label="Scope to month"
          />
        </label>
        {month || searchInput ? (
          <button
            type="button"
            className="cfo-shell-chip"
            onClick={() => {
              setSearchInput('')
              setSearch('')
              setMonth('')
            }}
          >
            Clear
          </button>
        ) : null}
      </CfoControlRow>
      {error ? (
        <section className="cfo-gl-state rise" role="alert">
          <strong>GL summary failed to load.</strong>
          <span>{error}</span>
        </section>
      ) : null}
      {loading && !summary ? <CfoLoadingNote label="Loading governed GL summary…" compact /> : null}
      {summary ? (
        <>
          <section className="cfo-gl-totals rise" aria-label="GL summary totals">
            <article className="cfo-gl-total-card">
              <span>Matching rows</span>
              <strong>{summary.row_count.toLocaleString()}</strong>
            </article>
            {Object.entries(summary.totals || {}).slice(0, 3).map(([label, value]) => (
              <article className="cfo-gl-total-card" key={label}>
                <span>{glDetailColumnLabel(label)}</span>
                <strong>{formatGlNumber(Number(value) || 0)}</strong>
              </article>
            ))}
          </section>
          {buckets.length ? (
            <section className="cfo-gl-totals rise" aria-label="GL summary drivers">
              {buckets.map(([label, list]) => (
                <article className="cfo-gl-total-card" key={label}>
                  <span>{label}</span>
                  <strong>{summaryBucketLine(list)}</strong>
                </article>
              ))}
            </section>
          ) : null}
          <section className="cfo-gl-table-frame rise" aria-busy={loading}>
            <div className="cfo-financials-gl-modal-table">
              <table className="cfo-gl-table">
                <thead>
                  <tr>
                    {columns.length ? columns.map((column) => (
                      <th key={column} className={GL_DETAIL_NUMERIC_COLUMNS.has(column) ? 'num' : undefined}>{glDetailColumnLabel(column)}</th>
                    )) : <th>Detail</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? rows.map((row, rowIndex) => (
                    <tr key={`${row.txn_date || ''}-${row.num || ''}-${rowIndex}`}>
                      {columns.map((column) => (
                        <td key={column} className={GL_DETAIL_NUMERIC_COLUMNS.has(column) ? 'num' : undefined}>
                          {formatGlCell(row[column], column)}
                        </td>
                      ))}
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={Math.max(1, columns.length)} className="cfo-gl-empty">
                        {loading ? 'Loading…' : 'No GL rows match this search.'}
                      </td>
                    </tr>
                  )}
                </tbody>
                {rows.length && numericTotalColumns.length ? (
                  <tfoot>
                    <tr>
                      {columns.map((column, index) => (
                        GL_DETAIL_NUMERIC_COLUMNS.has(column)
                          ? <td key={column} className="num">{formatGlCell(summary.totals?.[column] || 0, column)}</td>
                          : <td key={column} className={index === 0 ? 'cfo-gl-total-label' : undefined}>{index === 0 ? 'Total' : ''}</td>
                      ))}
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
            <footer className="cfo-gl-scroll-footer">
              <span>{summary.row_count.toLocaleString()} matching rows</span>
              <span>
                {summary.limited || summary.row_count > rows.length
                  ? `Showing first ${rows.length.toLocaleString()} — narrow the search or month to see the rest`
                  : `Showing all ${rows.length.toLocaleString()}`}
              </span>
            </footer>
          </section>
        </>
      ) : null}
    </div>
  )
}

export default CfoGlDetailServerFallback
